import {Col, Row} from "reactstrap";
import {Fade, Paper} from "@mui/material";
import Form from "../../../base/form";
import React, {useCallback, useMemo, useState} from "react";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import {OutlinedDropdownForm} from "../../../base/dropdown/outlined_dropdown";
import Button from "../../../base/button/button";
import {deepEqual} from "../../../../../core/services/utils/utils";
import useIsMounted from "../../../../hooks/use-is-mounted";
import classnames from "classnames";
import AddressSelectionDialog from "../../../dialogs/address-selection";
import SaveOrderAddressDialog from "../../../dialogs/save-order-address";
import useCountryProvinceCache from "../../../../hooks/use-country-province-cache";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {MuiRFF, Yup} from '../../../form';
import EnvService from "../../../../../core/services/env";

const schema = Yup.object().shape({
    country: Yup.mixed().nullable().required(ValidateMessages.required),
    province: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.required),
    fullAddress: Yup.string().nullable().required(ValidateMessages.required),
    postalCode: Yup.string().nullable().required(ValidateMessages.required),
    city: Yup.string().nullable().required(ValidateMessages.required),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const initSaveOrderAddressDialog = {open: false, address: null};

const OrderInformationViewEditAddressesSection = ({
                                                      address,
                                                      updateAddress,
                                                      onAddressUpdated,
                                                      typeId,
                                                      loading,
                                                      customerId,
                                                      customerName,
                                                  }) => {
    const [updating, setUpdating] = useState(false);
    const [openSelectionDialog, setOpenSelectionDialog] = useState(false);
    const [saveOrderAddressDialogState, setSaveOrderAddressDialogState] = useState(initSaveOrderAddressDialog);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const {countries, onCountrySelected} = useCountryProvinceCache(address?.province?.countryId);

    /**@type {Record<string, any>}*/
    const initialValues = useMemo(() => {
        if (!address) return {};
        return {
            fullAddress: address?.fullAddress,
            city: address?.city,
            province: address?.province?.id,
            country: countries?.find(e => e.id === address?.province?.countryId),
            postalCode: address?.postalCode,
        };
    }, [address, countries])


    /**
     * Updates the address by preparing the object of the address and calling [updateAddressProps] callback.
     * @type {function(*): Promise<void>}
     */
    const editAddress = useCallback(async (values) => {
        const province = countries?.find(e => Number(e.id) === Number(values?.country?.id))?.provinces?.find(e => e.id === values.province)
        if (!province)
            return;
        const address = {
            ...values,
            province: {
                ...province,
                country: {
                    ...values?.country,
                },
            },
            type: {id: typeId},
        }
        setUpdating(true);
        const resultFlag = await updateAddress(address);
        if (!isMounted())
            return;
        setUpdating(false);
        if (resultFlag)
            await onAddressUpdated?.();
        return resultFlag;
    }, [updateAddress, isMounted, typeId, countries, onAddressUpdated]);

    /**
     * Updates the address of this order with the selected address.
     * @type {function(*=): Promise<void>}
     */
    const onAddressSelected = useCallback(async (selectedAddress) => {
        const address = {
            ...selectedAddress,
            fullAddress: selectedAddress?.streetAddress,
        }
        setUpdating(true);
        const resultFlag = await updateAddress(address);
        if (!isMounted())
            return;
        setUpdating(false);
        if (resultFlag)
            await onAddressUpdated?.();
        return resultFlag;
    }, [updateAddress, isMounted, onAddressUpdated]);

    /**
     * Changes the value of province and country and if the provinces of that country is not yet loaded, gets them
     * from the server.
     * @type {function(*, *): void}
     */
    const onCountryChanged = useCallback((e, form) => {
        form.change(e.target.name, e.target.value);
        form.change('province', null);
        const countryId = e.target.value?.id;
        onCountrySelected(countryId);
    }, [onCountrySelected]);

    /**
     * Opens the save for later modal and saves the selected values to be used when user confirms the operation
     * @type {function(*): Promise<void>}
     */
    const onSaveForLater = useCallback(async (values) => {
        if (Object.values(await validate(values))?.some(e => !!e))
            return;
        const province = countries?.find(e => Number(e.id) === Number(values?.country?.id))?.provinces?.find(e => e.id === values.province)
        setSaveOrderAddressDialogState({
            open: true,
            address: {
                ...values,
                province: province,
            },
        });
    }, [countries])

    return (
        <>
            <Paper className='paper w-100 px-3 py-2'>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <div className='my-3 w-100'/>
                    {
                        loading &&
                        <>
                            <div
                                className='loading input row w-100 no-gutters d-flex align-items-center justify-content-around'>
                                <div className='w-48'/>
                                <div className='w-48'/>
                            </div>
                            <div className='loading input row w-100 no-gutters my-3 px-2'>
                                <div className='w-100'/>
                            </div>
                            <div
                                className='loading input row w-100 no-gutters d-flex align-items-center justify-content-around'>
                                <div className='w-48'/>
                                <div className='w-48'/>
                            </div>
                        </>
                    }
                    {
                        !loading &&
                        <Form
                            onSubmit={editAddress}
                            initialValues={initialValues}
                            validate={validate}
                            className='w-100 px-xl-5 px-3 '
                            render={({values, initialValues, form}) => {
                                const colClass = 'px-2 mb-4';
                                const provinces = !!values?.country
                                    ? countries?.find(e => e.id === values.country?.id)?.provinces ?? []
                                    : [];
                                const canProceed = !deepEqual(values, initialValues) && !loading && !updating;
                                return (
                                    <Row className='no-gutters justify-content-center w-100'>
                                        {
                                            operatorAccessLevels.packages.sales.manageCustomerAddresses &&
                                            <Col xs={12} className={classnames('d-flex justify-content-end', colClass)}>
                                                <Button
                                                    disabled={updating}
                                                    onClick={() => setOpenSelectionDialog(true)}
                                                    variant={'outlined'}
                                                    color={'primary'}
                                                >
                                                    {
                                                        updating
                                                            ? 'submitting...'
                                                            : 'Select from Users Addresses'
                                                    }
                                                </Button>
                                            </Col>
                                        }
                                        <Col xs={6} className={`${colClass}`}>
                                            <OutlinedDropdownForm
                                                label={'Country'}
                                                name={'country'}
                                                required={required.country}
                                                data={countries?.map(e => ({
                                                    value: e,
                                                    label: e.name,
                                                    id: e.id,
                                                })) ?? []}
                                                onChange={(e) => onCountryChanged(e, form)}
                                            />
                                        </Col>
                                        <Col xs={6} className={`${colClass}`}>
                                            <OutlinedDropdownForm
                                                label={!values.country ? 'Province / State' : values.country?.name === EnvService.USATitle ? "State" : 'Province'}
                                                name={'province'}
                                                required={required.province}
                                                renderValue={v => provinces.find(e => e.id === v)?.name ?? provinces.find(e => e.id === v)?.code}
                                                data={provinces?.map(e => ({
                                                    value: e.id,
                                                    label: e.name ?? e.code,
                                                    id: e.id,
                                                })) ?? []}
                                            />
                                        </Col>
                                        <Col xs={12} className={colClass}>
                                            <OutlinedInputForm
                                                label={'Street Address'}
                                                name={`fullAddress`}
                                                required={required.fullAddress}
                                            />
                                        </Col>
                                        <Col xs={8} className={colClass}>
                                            <OutlinedInputForm
                                                label={'City'}
                                                name={`city`}
                                                required={required.city}
                                            />
                                        </Col>
                                        <Col xs={4} className={colClass}>
                                            <OutlinedInputForm
                                                label={!values.country ? 'Postal/Zip Code' : values.country?.name === EnvService.USATitle ? "Zip Code" : 'Postal Code'}
                                                name={`postalCode`}
                                                required={required.postalCode}
                                            />
                                        </Col>
                                        <Col xs={12}
                                             className={`px-2 d-flex align-items-center justify-content-center no-gutters`}>
                                            <div className={'d-flex flex-column w-100 align-items-center'}>
                                                <Button
                                                    disabled={!canProceed}
                                                    type={'submit'}
                                                    color={'primary'}
                                                    variant={'contained'}
                                                    className='w-50 w-md-25 mb-2 py-2-5'
                                                >
                                                    {
                                                        updating
                                                            ? "processing..."
                                                            : " Save changes"
                                                    }
                                                </Button>
                                                {
                                                    operatorAccessLevels.packages.sales.manageCustomerAddresses &&
                                                    <Fade in={canProceed}>
                                                        <Button
                                                            disabled={!canProceed}
                                                            variant={'text'}
                                                            color={'primary'}
                                                            className='w-50 w-md-25'
                                                            onClick={() => onSaveForLater(values)}
                                                        >
                                                            Save it for Later
                                                        </Button>
                                                    </Fade>
                                                }
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            }}
                        />
                    }
                    <div className='my-3 w-100'/>
                </Row>
            </Paper>
            {
                operatorAccessLevels.packages.sales.manageCustomerAddresses &&
                <AddressSelectionDialog
                    updating={updating}
                    type={typeId}
                    onSelect={onAddressSelected}
                    open={openSelectionDialog}
                    setOpen={setOpenSelectionDialog}
                    customerId={customerId}
                />
            }
            {
                operatorAccessLevels.packages.sales.manageCustomerAddresses &&
                <SaveOrderAddressDialog
                    typeId={typeId}
                    customerId={customerId}
                    customerName={customerName}
                    open={saveOrderAddressDialogState.open}
                    address={saveOrderAddressDialogState.address}
                    close={() => setSaveOrderAddressDialogState(initSaveOrderAddressDialog)}
                    onAddressCreated={onAddressSelected}
                />
            }
        </>
    );
}

export default OrderInformationViewEditAddressesSection;
